import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { orpc } from "~/lib/orpc-client";
import { requireAuth } from "~/lib/auth-guard";
import { Skeleton, useToast } from "@alchema/ui";
import { z } from "zod";
import type { AnswerFeedback } from "@alchema/shared";
import { AiGeneratedBadge } from "~/components/AiGeneratedBadge";
import * as m from "~/paraglide/messages";

const searchSchema = z.object({
  sessionId: z.string().uuid(),
});

export const Route = createFileRoute("/interview-prep/practice")({
  beforeLoad: async () => { await requireAuth(); },
  validateSearch: (search) => searchSchema.parse(search),
  component: PracticePage,
});

const CATEGORY_DOT_COLORS: Record<string, string> = {
  behavioral: "var(--accent)",
  technical: "var(--status-purple, #a78bfa)",
  situational: "var(--warning)",
  "company-specific": "var(--success)",
};

const DIFFICULTY_DOT_COLORS: Record<string, string> = {
  easy: "var(--success)",
  medium: "var(--warning)",
  hard: "var(--error)",
};

function scoreColor(score: number): { color: string; bg: string; border: string } {
  if (score >= 8) return { color: "var(--success)", bg: "var(--status-success-bg, rgba(34,197,94,0.1))", border: "var(--success)" };
  if (score >= 5) return { color: "var(--warning)", bg: "var(--status-warning-bg, rgba(234,179,8,0.1))", border: "var(--warning)" };
  return { color: "var(--error)", bg: "var(--status-error-bg, rgba(239,68,68,0.1))", border: "var(--error)" };
}

/**
 * AI answer-feedback panel with EU AI Act Article 50 disclosure. The badge
 * renders adjacent to the AI-generated score/strengths/improvements/revised
 * answer produced by `orpc.interview.submitAnswer`.
 */
export function AnswerFeedbackPanel({ feedback }: { feedback: AnswerFeedback }) {
  const [showRevised, setShowRevised] = React.useState(false);
  const sc = scoreColor(feedback.score);
  return (
    <>
      {/* AI transparency badge (EU AI Act Article 50) */}
      <div className="flex justify-end">
        <AiGeneratedBadge />
      </div>
      {/* Score */}
      <div
        className="rounded-xl p-5 flex items-center gap-4"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2"
          style={{ borderColor: sc.border, background: sc.bg }}
        >
          <span className="text-xl font-bold tabular-nums" style={{ color: sc.color }}>
            {feedback.score}
          </span>
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: "var(--text)" }}>
            {feedback.score >= 8
              ? m.interview_practice_score_excellent()
              : feedback.score >= 5
                ? m.interview_practice_score_good()
                : m.interview_practice_score_needs_improvement()}
          </p>
          <p className="text-xs" style={{ color: "var(--text-muted)" }}>
            {m.interview_practice_score_label({ score: feedback.score })}
          </p>
        </div>
      </div>

      {/* Strengths */}
      <div
        className="rounded-xl p-5"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: "var(--success)" }}>
          {m.interview_practice_strengths()}
        </h3>
        <ul className="space-y-2">
          {feedback.strengths.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: "var(--success)" }} />
              {s}
            </li>
          ))}
        </ul>
      </div>

      {/* Improvements */}
      <div
        className="rounded-xl p-5"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: "var(--warning)" }}>
          {m.interview_practice_improvements()}
        </h3>
        <ul className="space-y-2">
          {feedback.improvements.map((s, i) => (
            <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: "var(--warning)" }} />
              {s}
            </li>
          ))}
        </ul>
      </div>

      {/* Revised Answer (collapsible) */}
      <div
        className="rounded-xl p-5"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <button
          onClick={() => setShowRevised(!showRevised)}
          className="flex w-full items-center justify-between"
          aria-expanded={showRevised}
        >
          <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>
            {m.interview_practice_suggested_answer()}
          </h3>
          <svg
            className={`h-4 w-4 transition ${showRevised ? "rotate-180" : ""}`}
            style={{ color: "var(--text-muted)" }}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </button>
        {showRevised && (
          <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>
            {feedback.revisedAnswer}
          </p>
        )}
      </div>
    </>
  );
}

function PracticePage() {
  const { sessionId } = Route.useSearch();
  const { toast } = useToast();
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [answer, setAnswer] = React.useState("");
  const [feedback, setFeedback] = React.useState<AnswerFeedback | null>(null);
  const [completedFeedback, setCompletedFeedback] = React.useState<Map<number, AnswerFeedback>>(new Map());

  const sessionQuery = useQuery(
    orpc.interview.getSession.queryOptions({ input: { sessionId } }),
  );

  const submitMutation = useMutation(
    orpc.interview.submitAnswer.mutationOptions({
      onSuccess: (data) => {
        if (data.feedback) {
          setFeedback(data.feedback);
          setCompletedFeedback((prev) => new Map(prev).set(currentIndex, data.feedback!));
        }
      },
      onError: (err) => toast(err.message || m.interview_practice_toast_submit_error(), "error"),
    }),
  );

  // Initialize completed feedback from existing answers
  React.useEffect(() => {
    if (sessionQuery.data?.answers) {
      const map = new Map<number, AnswerFeedback>();
      for (const a of sessionQuery.data.answers) {
        if (a.feedback) {
          map.set(a.questionIndex, a.feedback);
        }
      }
      setCompletedFeedback(map);
      // Start at first unanswered question
      const answeredIndices = new Set(sessionQuery.data.answers.map((a) => a.questionIndex));
      const totalQuestions = sessionQuery.data.questions.length;
      for (let i = 0; i < totalQuestions; i++) {
        if (!answeredIndices.has(i)) {
          setCurrentIndex(i);
          break;
        }
      }
    }
  }, [sessionQuery.data?.answers]);

  if (sessionQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-2 w-full rounded-full" />
        <Skeleton className="h-36 rounded-xl" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  if (sessionQuery.isError) {
    return (
      <div
        className="rounded-xl p-8 text-center"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          {m.interview_error_load_session()}
        </p>
        <button onClick={() => sessionQuery.refetch()} className="btn btn-ghost btn-sm mt-3">
          {m.interview_error_retry()}
        </button>
      </div>
    );
  }

  if (!sessionQuery.data) {
    return (
      <div
        className="rounded-xl p-6 text-center"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>{m.interview_practice_session_not_found()}</p>
      </div>
    );
  }

  const session = sessionQuery.data;
  const questions = session.questions;
  const totalQuestions = questions.length;
  const isComplete = currentIndex >= totalQuestions;

  // Summary view when all questions are answered
  if (isComplete) {
    const scores = Array.from(completedFeedback.values()).map((f) => f.score);
    const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    const avgColors = scoreColor(avgScore);

    // Category scores
    const categoryScores = new Map<string, number[]>();
    for (const [idx, fb] of completedFeedback) {
      const q = questions[idx];
      if (q) {
        const existing = categoryScores.get(q.category) ?? [];
        existing.push(fb.score);
        categoryScores.set(q.category, existing);
      }
    }

    return (
      <div className="space-y-4 animate-fade-up">
        {/* Overall score */}
        <div
          className="rounded-xl p-6 text-center"
          style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
        >
          <h2 className="text-base font-semibold" style={{ color: "var(--text)" }}>{m.interview_practice_complete_title()}</h2>
          <div
            className="mx-auto mt-4 flex h-20 w-20 items-center justify-center rounded-full border-2"
            style={{ borderColor: avgColors.border, background: avgColors.bg }}
          >
            <span className="text-2xl font-bold tabular-nums" style={{ color: avgColors.color }}>
              {avgScore.toFixed(1)}
            </span>
          </div>
          <p className="mt-2 text-xs" style={{ color: "var(--text-muted)" }}>{m.interview_practice_average_score()}</p>
        </div>

        {/* Category breakdown */}
        <div
          className="rounded-xl p-5"
          style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
        >
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
            {m.interview_practice_category_performance()}
          </h3>
          <div className="space-y-3">
            {Array.from(categoryScores.entries()).map(([category, catScores]) => {
              const avg = catScores.reduce((a, b) => a + b, 0) / catScores.length;
              const dotColor = CATEGORY_DOT_COLORS[category] ?? "var(--text-muted)";
              const barColor = avg >= 7 ? "var(--success)" : avg >= 4 ? "var(--warning)" : "var(--error)";
              return (
                <div key={category} className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 w-32 shrink-0">
                    <span className="inline-block h-1.5 w-1.5 rounded-full shrink-0" style={{ background: dotColor }} />
                    <span className="text-xs capitalize" style={{ color: "var(--text-secondary)" }}>{category}</span>
                  </span>
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--bg-secondary)" }}>
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${(avg / 10) * 100}%`, background: barColor }}
                    />
                  </div>
                  <span className="w-8 text-right text-xs font-medium tabular-nums" style={{ color: "var(--text-secondary)" }}>
                    {avg.toFixed(1)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const hasAnswered = !!feedback || completedFeedback.has(currentIndex);
  const currentFeedback = feedback ?? completedFeedback.get(currentIndex) ?? null;

  const handleSubmit = () => {
    if (answer.length < 10) {
      toast(m.interview_practice_answer_min_length(), "error");
      return;
    }
    submitMutation.mutate({
      sessionId,
      questionIndex: currentIndex,
      answer,
    });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => prev + 1);
    setAnswer("");
    setFeedback(null);
    // `showRevised` now lives in <AnswerFeedbackPanel>, which unmounts while
    // there is no feedback and remounts collapsed for the next answer.
  };

  const progressPct = ((currentIndex + 1) / totalQuestions) * 100;

  return (
    <div className="space-y-5">
      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            {m.interview_practice_question_progress({ current: currentIndex + 1, total: totalQuestions })}
          </span>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            {m.interview_practice_answered_count({ count: completedFeedback.size })}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: "var(--bg-secondary)" }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%`, background: "var(--accent)" }}
          />
        </div>
      </div>

      {/* Question card */}
      <div
        className="rounded-xl p-6"
        style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
      >
        <div className="flex items-center gap-3 mb-4">
          <span className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-secondary)" }}>
            <span
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: CATEGORY_DOT_COLORS[currentQuestion.category] ?? "var(--text-muted)" }}
            />
            <span className="capitalize">{currentQuestion.category}</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: "var(--text-muted)" }}>
            <span
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ background: DIFFICULTY_DOT_COLORS[currentQuestion.difficulty] ?? "var(--text-muted)" }}
            />
            <span className="capitalize">{currentQuestion.difficulty}</span>
          </span>
        </div>
        <p className="text-base font-medium leading-relaxed" style={{ color: "var(--text)" }}>
          {currentQuestion.question}
        </p>
      </div>

      {/* Answer input */}
      {!hasAnswered && (
        <div
          className="rounded-xl p-5"
          style={{ border: "1px solid var(--border)", background: "var(--surface)" }}
        >
          <label className="block text-xs font-medium mb-2" style={{ color: "var(--text-secondary)" }}>
            {m.interview_practice_your_answer()}
          </label>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            rows={6}
            placeholder={m.interview_practice_placeholder()}
            className="w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 resize-none"
            style={{
              border: "1px solid var(--border)",
              background: "var(--bg-secondary)",
              color: "var(--text)",
            }}
          />
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              {m.interview_practice_char_count({ count: answer.length })}
            </span>
            <button
              onClick={handleSubmit}
              disabled={answer.length < 10 || submitMutation.isPending}
              className="btn btn-primary btn-sm"
            >
              {submitMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <svg aria-hidden="true" className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {m.interview_practice_evaluating()}
                </span>
              ) : (
                m.interview_practice_submit()
              )}
            </button>
          </div>
        </div>
      )}

      {/* Feedback */}
      {currentFeedback && (
        <div className="space-y-3 animate-fade-up">
          <AnswerFeedbackPanel feedback={currentFeedback} />

          {/* Next button */}
          <div className="flex justify-end pt-1">
            <button
              onClick={handleNext}
              className="btn btn-primary"
            >
              {currentIndex < totalQuestions - 1 ? m.interview_practice_next_question() : m.interview_practice_view_summary()}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
